import React from 'react';
import { SectionCard } from '../../common/SectionCard';
import { EmptyState } from '../../common/EmptyState';
import { formatDate } from '../../../utils/format';
import { cn } from '../../../utils/cn';
import type { ReadinessDetail } from '../../../types/domain';
const resultClasses = {
  passed: 'border-uae-leaf/30 bg-uae-leaf/10 text-uae-leaf',
  issues: 'border-uae-gold/40 bg-uae-gold/12 text-uae-bronze',
  scheduled: 'border-border bg-muted text-foreground'
};
export function ReadinessPanel({
  readiness
}: {
  readiness?: ReadinessDetail;
}) {
  if (!readiness) {
    return <EmptyState title="Readiness assessment not started" description="Readiness domains, test events and integrated assurance appear once the record clears the Plan & Build gate." data-id="element-985" data-mp="2bpvxnq88c1ro" />;
  }
  const belowThreshold = readiness.domains.filter(d => d.score < d.threshold);
  return <div className="space-y-4" data-id="element-986" data-mp="1nzrs3dbhdry1">
      <SectionCard title="Readiness domains" description="Each domain must reach its threshold before the gate can clear" actions={<span className={cn('rounded-sm border px-1.5 py-0.5 text-[11px]', belowThreshold.length === 0 ? 'border-uae-leaf/30 bg-uae-leaf/10 text-uae-leaf' : 'border-uae-red/30 bg-uae-red/10 text-uae-red')} data-id="element-988" data-mp="28zsz5xpx0e4u">
            {belowThreshold.length === 0 ? 'All domains at threshold' : `${belowThreshold.length} below threshold`}
          </span>} bodyClassName="p-0" data-id="element-987" data-mp="1nyqilda45dif">
        <ul className="divide-y divide-border" data-id="element-989" data-mp="1l9f561nrs57m">
          {readiness.domains.map(domain => {
          const below = domain.score < domain.threshold;
          return <li key={domain.id} className="px-4 py-3" data-id="element-990" data-mp="3fnzitudob0hi">
                <div className="flex items-center justify-between gap-3" data-id="element-991" data-mp="3fng38kbhwo6n">
                  <div className="min-w-0" data-id="element-992" data-mp="3apy2hh7mtf9q">
                    <p className="truncate text-[13px] font-medium" data-id="element-993" data-mp="3amsa9t3phx9s">{domain.label}</p>
                    <p className="text-[11px] text-muted-foreground" data-id="element-994" data-mp="18yecmiyft32g">{domain.owner}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2" data-id="element-995" data-mp="1dsinhdatpv8t">
                    <span className={cn('font-mono text-[12px]', below ? 'text-uae-red' : 'text-foreground')} data-id="element-996" data-mp="z2uhvzvm0afa">
                      {domain.score}
                    </span>
                    <span className="font-mono text-[11px] text-muted-foreground" data-id="element-997" data-mp="1wo8q92qkyvnr">
                      / {domain.threshold}
                    </span>
                  </div>
                </div>
                <div className="relative mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted" data-id="element-998" data-mp="1mq2qaqoc3zlj">
                  <div className={cn('h-full rounded-full', below ? 'bg-uae-red' : 'bg-uae-green')} style={{
                width: `${domain.score}%`
              }} data-id="element-999" data-mp="79akr51neqft" />
                  <span className="absolute top-0 h-full w-0.5 bg-uae-ink/50" style={{
                left: `${domain.threshold}%`
              }} aria-hidden="true" data-id="element-1000" data-mp="3rekpvvg1d2f" />
                </div>
              </li>;
        })}
        </ul>
      </SectionCard>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]" data-id="element-1001" data-mp="8su1vcui2zfj">
        <SectionCard title="Test events & rehearsals" bodyClassName="p-0" data-id="element-1002" data-mp="3h6szeix4esv5">
          <ul className="divide-y divide-border" data-id="element-1003" data-mp="3h4zousjdzohv">
            {readiness.tests.map(test => <li key={test.id} className="flex items-center justify-between gap-3 px-4 py-2.5" data-id="element-1004" data-mp="3c6eq6xjs9cvj">
                <div className="min-w-0" data-id="element-1005" data-mp="3qyyrrtrabcav">
                  <p className="truncate text-[13px]" data-id="element-1006" data-mp="3m65nt83n47zu">{test.label}</p>
                  <p className="text-[11px] text-muted-foreground" data-id="element-1007" data-mp="2ygl0ye4d8qla">{formatDate(test.on)}</p>
                </div>
                <span className={cn('shrink-0 rounded-sm border px-1.5 py-0.5 text-[11px] capitalize', resultClasses[test.result])} data-id="element-1008" data-mp="2ti9so7tpyo56">
                  {test.result}
                </span>
              </li>)}
          </ul>
        </SectionCard>

        <SectionCard title="Integrated assurance" data-id="element-1009" data-mp="25pqcwbv7jbrr">
          <p className="text-xl font-semibold" data-id="element-1010" data-mp="2aiazotfp4ols">
            {readiness.assuranceSignedOff ? 'Signed off' : 'Held'}
          </p>
          <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground" data-id="element-1011" data-mp="2fg2wo1v770wu">
            {readiness.assuranceSignedOff ? 'The independent assurance panel has signed off operational readiness across all domains.' : `The independent assurance panel is holding sign-off${belowThreshold.length > 0 ? ` until ${belowThreshold.map(d => d.label.toLowerCase()).join(' and ')} reach threshold.` : '.'}`}
          </p>
        </SectionCard>
      </div>
    </div>;
}